// Page builder integration panel (MOD-14 / Blok F).
//
// Owns the block editing surface for an existing page: the `useBuilder` toggle
// (controlled by the parent so it can swap the classic Tiptap body in/out), the
// lazily-loaded Puck canvas, a read-only structural preview, and the blocks
// save. Saving blocks is a SEPARATE call from the page update. GET warnings[]
// (dangling references) surface as an Alert; BE PB_INVALID_BLOCK / PB_REF_NOT_FOUND
// errors are shown with their block-scoped message.

import { Suspense, lazy, useMemo, useState } from 'react';
import { Alert, Button, Card, Modal, Space, Spin, Switch, Typography } from 'antd';
import { Eye, Save } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { NormalizedApiError } from '@api/errors';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { ErrorCode } from '@shared/constants/errorCodes';
import { recordAuditAction } from '@features/audit/recordAction';
import { pageBlocksApi, MAX_PAGE_BLOCKS, type PageBlocksResult } from '@api/pageBlocks';
import { componentsApi } from '@api/components';
import { blocksToPuckData, puckDataToBlocks, type PuckData } from '../blocks/mapping';
import { BlockPreview } from '../blocks/BlockPreview';
import { toRuntimeComponentDef } from '../builder/runtimeComponents';

const PuckPageBuilder = lazy(() => import('../builder/PuckPageBuilder'));

const { Text } = Typography;

interface PageBuilderPanelProps {
  pageId: string;
  canEdit: boolean;
  useBuilder: boolean;
  onUseBuilderChange: (value: boolean) => void;
  initial: PageBlocksResult;
  pageTitle: string;
}

export function PageBuilderPanel({
  pageId,
  canEdit,
  useBuilder,
  onUseBuilderChange,
  initial,
  pageTitle,
}: PageBuilderPanelProps) {
  const { t } = useTranslation(['pages', 'common']);
  const qc = useQueryClient();
  const [puckData, setPuckData] = useState<PuckData>(() => blocksToPuckData(initial.blocks));
  const [previewOpen, setPreviewOpen] = useState(false);
  const [blockError, setBlockError] = useState<string | null>(null);

  // K-18 — published Component Studio bileşenleri paleti (builder açıkken çekilir).
  // Palet published-only; runtime tanımlar Puck config'ine merge edilir.
  const { data: palette } = useQuery({
    queryKey: ['component-palette'],
    queryFn: () => componentsApi.palette(),
    staleTime: 60_000,
    enabled: useBuilder,
  });
  const runtimeComponents = useMemo(
    () => (palette ?? []).map(toRuntimeComponentDef),
    [palette],
  );

  const blocks = useMemo(() => puckDataToBlocks(puckData), [puckData]);
  const overLimit = blocks.length > MAX_PAGE_BLOCKS;

  const saveMutation = useMutation({
    mutationFn: () => pageBlocksApi.update(pageId, { useBuilder, blocks }),
    onSuccess: async () => {
      setBlockError(null);
      toast.success(t('builder.toast.saved'));
      // BE blocks-PUT hem `useBuilder`'ı çevirir hem yeni versiyon snapshot'ı yazar →
      // blok listesinin yanında sayfa detayını ve versiyon panelini de tazele.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['page-blocks', pageId] }),
        qc.invalidateQueries({ queryKey: ['pages', pageId] }),
        qc.invalidateQueries({ queryKey: ['page-versions', pageId] }),
      ]);
      void recordAuditAction({
        action: 'page.blocks.update',
        resource: 'page',
        resourceId: pageId,
        summary: t('builder.audit.saved', { title: pageTitle, count: blocks.length }),
      });
    },
    onError: (err: unknown) => {
      if (
        err instanceof NormalizedApiError &&
        (err.code === ErrorCode.PB_INVALID_BLOCK || err.code === ErrorCode.PB_REF_NOT_FOUND)
      ) {
        // BE message is already block-scoped — surface it verbatim.
        setBlockError(err.message);
        toast.error(t('builder.toast.invalid'));
        return;
      }
      setBlockError(null);
      toast.error(resolveApiErrorMessage(err));
    },
  });

  return (
    <Card
      title={t('builder.title')}
      extra={
        <Space>
          <Button
            icon={<Eye size={14} />}
            onClick={() => setPreviewOpen(true)}
            disabled={blocks.length === 0}
          >
            {t('builder.preview.open')}
          </Button>
          <Button
            type="primary"
            icon={<Save size={14} />}
            loading={saveMutation.isPending}
            disabled={!canEdit || overLimit}
            onClick={() => saveMutation.mutate()}
          >
            {t('builder.save')}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Switch
            checked={useBuilder}
            onChange={onUseBuilderChange}
            disabled={!canEdit}
            data-testid="builder-toggle"
          />
          <div>
            <Text strong>{t('builder.useBuilder')}</Text>
            <div style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>
              {t('builder.useBuilderHint')}
            </div>
          </div>
        </div>

        {initial.warnings.length > 0 ? (
          <Alert
            type="warning"
            showIcon
            message={t('builder.warnings.title')}
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {initial.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            }
          />
        ) : null}

        {blockError ? (
          <Alert
            type="error"
            showIcon
            closable
            onClose={() => setBlockError(null)}
            message={t('builder.errors.title')}
            description={blockError}
            data-testid="builder-error"
          />
        ) : null}

        {overLimit ? (
          <Alert type="error" showIcon message={t('builder.errors.tooMany', { max: MAX_PAGE_BLOCKS })} />
        ) : null}

        {useBuilder ? (
          <Suspense
            fallback={
              <div style={{ padding: 48, textAlign: 'center' }}>
                <Spin />
                <div style={{ marginTop: 12, color: 'var(--ds-text-secondary)' }}>
                  {t('builder.loading')}
                </div>
              </div>
            }
          >
            <PuckPageBuilder
              data={puckData}
              onChange={setPuckData}
              runtimeComponents={runtimeComponents}
            />
          </Suspense>
        ) : (
          <Alert type="info" showIcon message={t('builder.classicActive')} />
        )}
      </Space>

      <Modal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        title={t('builder.preview.title')}
        width={900}
        footer={null}
        destroyOnHidden
      >
        <BlockPreview blocks={blocks} />
      </Modal>
    </Card>
  );
}
